import { useState, useEffect, useCallback, useMemo } from "react";
import { arrayMove } from "@dnd-kit/sortable";
import { persistStore } from "@/persist-store";
import {
  WIDGET_REGISTRY,
  type DashboardLayout,
  type WidgetDefinition,
  type WidgetSize,
} from "./constants";
import {
  getDefaultLayout,
  mergeWithRegistry,
} from "./dashboard-layout-helpers";

const STORAGE_KEY = "dashboard_widget_layout";

export function useDashboardLayout() {
  const [layout, setLayout] = useState<DashboardLayout>(getDefaultLayout);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    persistStore.getLocal<DashboardLayout>(STORAGE_KEY).then((saved) => {
      if (saved) {
        setLayout(mergeWithRegistry(saved));
      }
      setLoaded(true);
    });
  }, []);

  const persist = useCallback((next: DashboardLayout) => {
    setLayout(next);
    persistStore.putLocal(STORAGE_KEY, next, { persistOnLogout: true });
  }, []);

  const registryMap = useMemo(
    () => new Map<string, WidgetDefinition>(WIDGET_REGISTRY.map((w) => [w.id, w])),
    [],
  );

  const visibleWidgets = useMemo(
    () =>
      layout
        .filter((item) => item.enabled)
        .sort((a, b) => a.order - b.order)
        .map((item) => ({ layout: item, definition: registryMap.get(item.id)! }))
        .filter((entry) => entry.definition),
    [layout, registryMap],
  );

  const allWidgets = useMemo(
    () =>
      [...layout]
        .sort((a, b) => a.order - b.order)
        .map((item) => ({ layout: item, definition: registryMap.get(item.id)! }))
        .filter((entry) => entry.definition),
    [layout, registryMap],
  );

  const handleDragEnd = useCallback(
    (activeId: string, overId: string) => {
      if (activeId === overId) return;
      const enabled = layout
        .filter((item) => item.enabled)
        .sort((a, b) => a.order - b.order);
      const fromIndex = enabled.findIndex((item) => item.id === activeId);
      const toIndex = enabled.findIndex((item) => item.id === overId);
      if (fromIndex < 0 || toIndex < 0) return;

      const reordered = arrayMove(enabled, fromIndex, toIndex);
      const disabled = layout.filter((item) => !item.enabled);
      const next = [...reordered, ...disabled].map((item, i) => ({
        ...item,
        order: i,
      }));
      persist(next);
    },
    [layout, persist],
  );

  const toggleWidget = useCallback(
    (id: string) => {
      const next = layout.map((item) =>
        item.id === id ? { ...item, enabled: !item.enabled } : item,
      );
      persist(next);
    },
    [layout, persist],
  );

  const setWidgetSize = useCallback(
    (id: string, size: WidgetSize) => {
      const next = layout.map((item) =>
        item.id === id ? { ...item, size } : item,
      );
      persist(next);
    },
    [layout, persist],
  );

  const resetLayout = useCallback(() => {
    persist(getDefaultLayout());
  }, [persist]);

  return {
    layout,
    loaded,
    visibleWidgets,
    allWidgets,
    handleDragEnd,
    toggleWidget,
    setWidgetSize,
    resetLayout,
  };
}
